import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Portal,
  Spinner,
} from '@chakra-ui/react';
import { LuListFilter, LuPlus } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import FilterValueSearch from './FilterValueSearch';
import { TOOLBAR_ICON_BUTTON } from '../toolbarButton';

// Re-exported so existing importers (FilterBar chip editing) keep working while
// the panels themselves live in their own module.
export {
  CheckboxFilterPanel,
  NumericFilterPanel,
  TextFilterPanel,
  DateFilterPanel,
  renderFilterPanel,
} from './filterPanels';
export { getFilterIcon } from './filterIcons';

/**
 * FilterMenu — the entry point to filtering.
 *
 * The popover body is value-first (see FilterValueSearch): you type a value,
 * not a column. A Popover rather than a Chakra Menu, because Menu swallows the
 * arrow keys the suggestion list needs.
 *
 * @param {string} variant - 'button' (labelled Filter), 'icon' (+ in the chip row),
 *                           'search-icon' (compact toolbar icon), 'text'
 */
const FilterMenu = ({
  filterOptions = [],
  filterOptionsLoading = false,
  selectedFilters = [],
  onAddFilter,
  onAppendFilter,
  onUpdateFilter,
  onRemoveFilter,
  onApplySuggestion,
  onRemoveLastFilter,
  onMenuOpen,
  timePresets = [],
  variant = 'button',
  isAdvancedMode = false,
  onEnableAdvancedMode,
  onOpenAdvancedPicker,
  fetchSuggestions,
  onAiFilter,
  // Advanced filter only: the menu's first row adds a new group, as Linear's
  // "Add filter group" does.
  onAddFilterGroup,
  // ⌘F opens this menu, in place of the command menu the Sidebar opens with it.
  openOnFind = false,
}) => {
  const { t } = useTranslation('filters');
  const [isOpen, setIsOpen] = useState(false);
  // Chakra focuses the popover content on open, which would take the caret out
  // of the search box; naming the input as the initial focus keeps it there.
  const searchRef = useRef(null);

  const open = useCallback(() => { setIsOpen(true); onMenuOpen?.(); }, [onMenuOpen]);
  const close = () => setIsOpen(false);

  // On document, so it runs before the Sidebar's listener on window, which
  // leaves a key already handled alone.
  useEffect(() => {
    if (!openOnFind) return undefined;
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'f') return;
      e.preventDefault();
      open();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openOnFind, open]);

  const trigger = () => {
    if (variant === 'icon') {
      // The + after the chips: a 24px square, as in Linear's filter row.
      return (
        <IconButton
          icon={<LuPlus size={14} />}
          size="xs"
          variant="ghost"
          w="24px"
          minW="24px"
          h="24px"
          borderRadius="6px"
          color="redesign.text.muted"
          _hover={{ color: 'redesign.heading', bg: 'redesign.bg.hover' }}
          aria-label={t('menu.addAria')}
        />
      );
    }
    if (variant === 'search-icon') {
      return (
        <IconButton
          {...TOOLBAR_ICON_BUTTON}
          icon={<LuListFilter size={14} />}
          aria-label={t('menu.buttonAria')}
        />
      );
    }
    if (variant === 'text') {
      // "+ Filter" inside the advanced panel: Linear's filled 24px pill, the
      // same fill as the and/or pill beside it.
      return (
        <Button
          size="xs"
          variant="ghost"
          h="24px"
          px="8px"
          borderRadius="6px"
          fontWeight="500"
          fontSize="12px"
          color="redesign.text"
          bg="redesign.bg.card"
          _dark={{ bg: 'redesign.border.strong' }}
          _hover={{ bg: 'redesign.bg.hover', color: 'redesign.heading' }}
          _focusVisible={{ boxShadow: '0 0 0 1px var(--chakra-colors-redesign-accent-blue)' }}
        >
          {t('menu.addFilterButton')}
        </Button>
      );
    }
    return (
      <Button
        size="sm"
        h="28px"
        variant="outline"
        fontWeight="500"
        fontSize={{ base: 'xs', md: '13px' }}
        color="redesign.text"
        bg="redesign.bg.surface"
        borderColor="redesign.border.strong"
        borderRadius="md"
        leftIcon={filterOptionsLoading ? <Spinner size="xs" /> : <LuListFilter size={14} />}
        _hover={{ bg: 'redesign.bg.hover', borderColor: 'redesign.text.muted' }}
        _active={{ bg: 'redesign.bg.card' }}
      >
        {t('menu.button')}
      </Button>
    );
  };

  const content = (
    <PopoverContent
      w="auto"
      minW="auto"
      p={0}
      zIndex={1400}
      bg="redesign.bg.popup"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="10px"
      // visible, so a field's panel can sit beside the menu
      overflow="visible"
      _focus={{ outline: 'none' }}
    >
      <FilterValueSearch
        searchRef={searchRef}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        selectedFilters={selectedFilters}
        onAddFilter={onAddFilter}
        onAppendFilter={onAppendFilter}
        onUpdateFilter={onUpdateFilter}
        onRemoveFilter={onRemoveFilter}
        onApplySuggestion={onApplySuggestion}
        onRemoveLastFilter={onRemoveLastFilter}
        timePresets={timePresets}
        onClose={close}
        onOpenAdvancedPicker={onOpenAdvancedPicker}
        onEnableAdvancedMode={onEnableAdvancedMode}
        isAdvancedMode={isAdvancedMode}
        fetchSuggestions={fetchSuggestions}
        onAiFilter={onAiFilter}
        onAddFilterGroup={onAddFilterGroup}
      />
    </PopoverContent>
  );

  return (
    <Popover
      isOpen={isOpen}
      onOpen={open}
      onClose={close}
      placement={variant === 'search-icon' ? 'bottom-end' : 'bottom-start'}
      isLazy
      gutter={6}
      closeOnBlur
      initialFocusRef={searchRef}
    >
      <PopoverTrigger>{trigger()}</PopoverTrigger>
      {/* Inside the advanced panel the menu stays in the panel's own tree, so
          picking from it doesn't count as leaving the panel, and the panel can
          close on an outside click as Linear's does. */}
      {variant === 'text' ? content : <Portal>{content}</Portal>}
    </Popover>
  );
};

export default FilterMenu;
